/**
 * MagazineOrders.js
 * Admin panel for print copy recent magazine orders.
 * Enables changing shipping delivery status, searching, and viewing shipping details.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { vendorService } from '../../../services/api';
import DateRangePicker from '../../../components/DateRangePicker';
import {
  ShoppingCart, RefreshCw, Eye, EyeOff, Search, Calendar, ChevronLeft, ChevronRight, Inbox, Truck, Loader
} from 'lucide-react';

const MagazineOrders = ({ toast }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPageState] = useState(() => {
    try {
      const saved = sessionStorage.getItem('admin_page_magazine_orders');
      return saved ? Math.max(1, parseInt(saved, 10)) : 1;
    } catch (e) {
      return 1;
    }
  });

  const setPage = useCallback((valOrFn) => {
    setPageState(prev => {
      const next = typeof valOrFn === 'function' ? valOrFn(prev) : valOrFn;
      try {
        sessionStorage.setItem('admin_page_magazine_orders', String(next));
      } catch (e) {}
      return next;
    });
  }, []);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Filters state
  const [filters, setFilters] = useState({
    search: '',
    delivery_status: 'ALL',
    start_date: '',
    end_date: ''
  });

  // Track expanded row IDs for Eye toggle details
  const [expandedIds, setExpandedIds] = useState(new Set());

  // Track status update in progress
  const [updatingId, setUpdatingId] = useState(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await vendorService.getAdminMagazineCopyOrders({
        page,
        page_size: 25,
        ...filters
      });
      if (res.success && res.data) {
        setOrders(res.data.orders || []);
        setTotalItems(res.data.total || 0);
        setTotalPages(Math.max(1, Math.ceil((res.data.total || 0) / 25)));
      } else {
        toast.error(res.error || 'Failed to fetch copy orders.');
      }
    } catch {
      toast.error('Failed to load copy orders.');
    } finally {
      setLoading(false);
    }
  }, [page, filters, toast]);

  useEffect(() => {
    setPage(1); // Reset page on filter changes
    setExpandedIds(new Set());
  }, [filters]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders, page]);

  const toggleExpandRow = (id) => {
    const next = new Set(expandedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setExpandedIds(next);
  };

  const handleDeliveryStatusChange = async (orderId, newStatus) => {
    setUpdatingId(orderId);
    try {
      const res = await vendorService.updateCopyOrderDelivery(orderId, newStatus);
      if (res.success) {
        toast.success(`Fulfillment updated to ${newStatus}!`);
        // Update local state row
        setOrders(prev => prev.map(o => o.id === orderId ? { ...o, delivery_status: newStatus } : o));
      } else {
        toast.error(res.error || 'Failed to update delivery status.');
      }
    } catch {
      toast.error('Failed to update delivery status.');
    } finally {
      setUpdatingId(null);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getDeliveryBadge = (status) => {
    switch (status) {
      case 'Delivered':
        return <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">Delivered</span>;
      case 'Shipped':
        return <span className="px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-bold">Shipped</span>;
      case 'Pending':
      default:
        return <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold">Pending Dispatch</span>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-100 pb-5 text-left">
        <div>
          <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
            <ShoppingCart size={18} className="text-[#2ab391]" />
            Magazine Customise Orders
          </h3>
          <p className="text-xs text-gray-400 mt-1 font-semibold">Track physical magazine copy sales, total revenues, and packaging dispatch status.</p>
        </div>
      </div>

      {/* Filter panel */}
      <div className="bg-gray-50/50 border border-gray-150 rounded-2xl p-4 grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 gap-4 items-center text-left">
        {/* Search */}
        <div className="relative">
          <input
            type="text"
            placeholder="Search name, phone..."
            value={filters.search}
            onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
            className="w-full bg-white border border-gray-200 rounded-xl pl-9 pr-4 py-2 text-xs font-semibold text-gray-800 focus:outline-none focus:border-[#2ab391] transition-all"
          />
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        </div>

        {/* Dispatch Selector */}
        <div className="relative">
          <select
            value={filters.delivery_status}
            onChange={(e) => setFilters(f => ({ ...f, delivery_status: e.target.value }))}
            className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2 text-xs font-bold text-gray-700 focus:outline-none focus:border-[#2ab391] appearance-none cursor-pointer"
          >
            <option value="ALL">Fulfillment Status: All</option>
            <option value="Pending">Pending Dispatch</option>
            <option value="Shipped">Shipped</option>
            <option value="Delivered">Delivered</option>
          </select>
          <Truck size={12} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>

        {/* Date Filter */}
        <div className="relative sm:col-span-2 flex items-center gap-2">
          <DateRangePicker
            startDate={filters.start_date}
            endDate={filters.end_date}
            onChange={(start, end) => {
              setFilters(f => ({ ...f, start_date: start, end_date: end }));
            }}
            placeholder="Select Date Range"
            className="w-full bg-white border border-gray-200 rounded-xl px-3 py-1.5 focus:outline-none"
          />
          {(filters.start_date || filters.end_date || filters.search || filters.delivery_status !== 'ALL') && (
            <button
              onClick={() => setFilters({ search: '', delivery_status: 'ALL', start_date: '', end_date: '' })}
              className="text-xs font-bold text-red-500 hover:text-red-600 pl-1 cursor-pointer shrink-0"
            >
              Reset
            </button>
          )}
        </div>
      </div>

      {/* Orders Table */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <RefreshCw className="animate-spin text-[#2ab391]" size={30} />
          <span className="text-sm font-semibold text-gray-500">Retrieving Orders Registry...</span>
        </div>
      ) : orders.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3 border border-dashed border-gray-200 rounded-2xl bg-white">
          <Inbox size={32} className="text-gray-300" />
          <div>
            <p className="text-sm font-bold text-gray-700">No copy orders found</p>
            <p className="text-xs text-gray-400 mt-1">Try expanding the date range filters.</p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-150 overflow-hidden shadow-xs">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50/50 border-b border-gray-150">
                    <th className="px-5 py-3.5 text-xs font-bold text-gray-500 uppercase tracking-wider">Customer Name</th>
                    <th className="px-5 py-3.5 text-xs font-bold text-gray-500 uppercase tracking-wider">Order Date</th>
                    <th className="px-5 py-3.5 text-xs font-bold text-gray-500 uppercase tracking-wider">Issue Copy</th>
                    <th className="px-5 py-3.5 text-xs font-bold text-gray-500 uppercase tracking-wider">Qty</th>
                    <th className="px-5 py-3.5 text-xs font-bold text-gray-500 uppercase tracking-wider">Amount Paid</th>
                    <th className="px-5 py-3.5 text-xs font-bold text-gray-500 uppercase tracking-wider">Delivery Status</th>
                    <th className="px-5 py-3.5 text-xs font-bold text-gray-500 uppercase tracking-wider text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 font-semibold text-sm text-gray-700">
                  {orders.map((o) => {
                    const isExpanded = expandedIds.has(o.id);
                    return (
                      <React.Fragment key={o.id}>
                        <tr className="hover:bg-gray-50/30 transition-colors">
                          <td className="px-5 py-3.5">
                            <div>
                              <p className="text-gray-800 font-bold">{o.name}</p>
                              <p className="text-[10px] text-gray-400 mt-0.5">{o.user_email}</p>
                            </div>
                          </td>
                          <td className="px-5 py-3.5 text-gray-600 font-medium">
                            {formatDate(o.created_at)}
                          </td>
                          <td className="px-5 py-3.5 text-gray-600 font-medium">
                            {o.magazine_month} {o.magazine_year}
                          </td>
                          <td className="px-5 py-3.5 text-gray-800 font-bold">
                            {o.quantity}
                          </td>
                          <td className="px-5 py-3.5 text-gray-800 font-bold text-emerald-600">
                            ₹{o.total_amount}
                          </td>
                          <td className="px-5 py-3.5">
                            {updatingId === o.id ? (
                              <Loader size={12} className="animate-spin text-[#2ab391]" />
                            ) : (
                              <div className="relative inline-block cursor-pointer">
                                <select
                                  value={o.delivery_status}
                                  onChange={(e) => handleDeliveryStatusChange(o.id, e.target.value)}
                                  className="text-xs font-bold text-gray-700 bg-white border border-gray-200 rounded px-2 py-0.5 focus:outline-none cursor-pointer focus:border-[#2ab391]"
                                >
                                  <option value="Pending">Pending</option>
                                  <option value="Shipped">Shipped</option>
                                  <option value="Delivered">Delivered</option>
                                </select>
                              </div>
                            )}
                          </td>
                          <td className="px-5 py-3.5 text-right">
                            <button
                              onClick={() => toggleExpandRow(o.id)}
                              className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-500 hover:text-gray-800 transition-colors cursor-pointer inline-flex items-center gap-1 text-xs"
                            >
                              {isExpanded ? <EyeOff size={14} /> : <Eye size={14} />}
                              <span>{isExpanded ? 'Hide' : 'Details'}</span>
                            </button>
                          </td>
                        </tr>

                        {/* Expanded details container */}
                        {isExpanded && (
                          <tr className="bg-gray-50/40">
                            <td colSpan={7} className="px-5 py-4 border-t border-gray-100">
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs font-semibold text-gray-600">
                                <div>
                                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Contact & Transaction</p>
                                  <p className="text-gray-800 font-bold mt-1">Recipient: {o.name}</p>
                                  <p className="text-gray-800 mt-0.5">Phone: {o.phone}</p>
                                  <p className="text-gray-800 mt-0.5">Email: {o.user_email}</p>
                                  <p className="text-gray-700 font-semibold text-xs mt-1">Razorpay ID: {o.razorpay_order_id}</p>
                                </div>
                                <div className="sm:col-span-2">
                                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Shipping Address</p>
                                  <p className="text-gray-800 font-bold mt-1">{o.delivery_address}</p>
                                  <p className="text-gray-800 mt-0.5">Pincode: {o.pincode}</p>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2 border-t border-gray-100 text-xs font-bold text-gray-500">
              <span>Showing Page {page} of {totalPages} ({totalItems} items)</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => fetchOrders()}
                  disabled={loading}
                  title="Reload current page"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 text-gray-600 hover:text-[#2ab391] hover:border-[#2ab391]/40 hover:bg-teal-50/50 rounded-lg font-bold text-xs transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
                  Reload
                </button>
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 cursor-pointer disabled:opacity-50"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 cursor-pointer disabled:opacity-50"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MagazineOrders;
